<script setup>
import {
  ref,
  toRef,
  shallowRef,
  computed,
  onMounted,
  onUnmounted,
  defineAsyncComponent,
} from 'vue';
import { useRsvpStore } from '@/stores/rsvp/rsvp.store';
import { PageFooter } from '@/components/clients/common';
import LangChooser from '@/components/common/LangChooser.vue';
import { Places, Wishes } from '@/components/clients/rsvp/components';
import formVersionList from '@/components/clients/rsvp/components/form/versions';
import GKSWidget from '../../common/GKSWidget.vue';
import HeaderWidget from '../../common/HeaderWidget.vue';
import ClockWidget from '../components/ClockWidget.vue';
import { useStateStore } from '@/stores/state.store';

const stateStore = useStateStore();
const rsvpStore = useRsvpStore();

const invitationText = toRef(() => rsvpStore.getLangData('invitationText'));
const invitationPurposeText = toRef(() =>
  rsvpStore.getLangData('invitationPurposeText'),
);
const textFont =
  rsvpStore.clientData.common.textFont &&
  rsvpStore.clientData.common.textFont.trim() !== ''
    ? rsvpStore.clientData.common.textFont
    : 'Alegreya';
const textFontB =
  rsvpStore.clientData.common.textFont &&
  rsvpStore.clientData.common.textFont.trim() !== ''
    ? rsvpStore.clientData.common.textFont
    : 'Lato';

const mainColor = toRef(() => rsvpStore.getColors.mainColor);
const backgroundColor = toRef(() => rsvpStore.getColors.backgroundColor);

const background = ref(null);

const weddingDate = computed(() => {
  const date = new Date(stateStore.getCommonData.eventDate);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const formattedDate = `${day}.${month}.${year}`;
  return formattedDate;
});

const imgColorFrom = computed(() => {
  return backgroundColor.value + 'FF';
});
const imgColorTo = computed(() => {
  return backgroundColor.value + '00';
});

function handleScroll(_evt) {
  const scrollY = window.scrollY;

  background.value.style.transform = `translate(0, ${Math.round(
    scrollY * 0.5,
  )}px)`;
}

const FormChooserVersion = shallowRef(null);
const componentLoaded = ref(false);

function pickVersion() {
  if (formVersionList[rsvpStore.clientData.common.formVersion]) {
    FormChooserVersion.value = defineAsyncComponent(
      () =>
        import(
          `../components/form/versions/${
            formVersionList[rsvpStore.clientData.common.formVersion]
          }/FormChooser.vue`
        ),
    );
    componentLoaded.value = true;
  }
}

onMounted(() => {
  pickVersion();
  handleScroll();
  document.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  document.removeEventListener('scroll', handleScroll);
});
</script>

<template>
  <div class="main">
    <div ref="background" class="background-image">
      <div
        class="image"
        :style="{
          backgroundImage: `url('${rsvpStore.getImages.rsvp.url}')`,
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundColor: `${backgroundColor}`,
        }"
      ></div>
      <div class="margin"></div>
    </div>
    <LangChooser
      :main-color="mainColor"
      :background-color="backgroundColor"
      :store="rsvpStore"
    />
    <div class="content">
      <div>
        <HeaderWidget
          :font="rsvpStore.getCommonData.headerTextFont"
          :her-name="rsvpStore.getCommonData.herName"
          :his-name="rsvpStore.getCommonData.hisName"
          :header-separator="rsvpStore.getCommonData.headerSeparator"
          :main-color="stateStore.getColors.mainColor"
        />
        <div class="text">
          <p v-if="invitationText">{{ invitationText }}</p>
          <p v-else>{{ $t('appCore.apps.rsvp.versions.1.invitationText') }}</p>
          <p v-if="invitationPurposeText">{{ invitationPurposeText }}</p>
          <p v-else>{{ $t('appCore.apps.rsvp.versions.1.0.placeText') }}</p>
          <p v-if="moreInfoText">
            <b>
              {{ moreInfoText }}
            </b>
          </p>
          <p v-else>
            <b>
              {{ weddingDate }}
              {{ $t('appCore.apps.rsvp.versions.1.yearAndHour') }}
              {{ rsvpStore.clientData.common.hour }}
              {{ $t('appCore.apps.rsvp.versions.1.in') }}
              {{ rsvpStore.getLangData('place') }}
            </b>
          </p>
          <p>
            {{ rsvpStore.getLangData('additionalInformation') }}
          </p>
        </div>
      </div>
      <Places v-if="rsvpStore.isAnyPlace()" />
      <GKSWidget v-if="stateStore.isGKS" />
      <FormChooserVersion v-if="componentLoaded" />
      <Wishes v-if="rsvpStore.isAnyWish" />
    </div>
    <ClockWidget
      v-if="rsvpStore.clientData.common.isClock"
      class="bottom-clock"
    />
    <div
      v-if="rsvpStore.getLangAllLangData().bottomYearText"
      class="foot core-default-h4"
    >
      {{ rsvpStore.getLangAllLangData().bottomYearText }}
    </div>
    <div v-else class="foot core-default-h4">2025</div>
  </div>
  <div class="background">
    <PageFooter />
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styleConstans';
@import './common';

.background {
  width: 100%;
  height: auto;
  background: v-bind(backgroundColor);
}

.background-image {
  position: absolute;
  overflow: hidden;
  width: 100%;
  height: 80vh;
  z-index: 2;

  .image {
    width: 100%;
    height: 100%;
    transition: 0.4s ease all;
  }

  .margin {
    position: absolute;
    bottom: -5px;
    background: v-bind(backgroundColor);
    width: 100%;
    height: 10px;
    z-index: 5;
  }
}

.background-image::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60%;
  background: linear-gradient(to top, v-bind(imgColorFrom), v-bind(imgColorTo));
}

.main {
  position: relative;
  top: 0;
  left: 0;
  color: v-bind(mainColor);
  background: v-bind(backgroundColor);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 25px;

  @media screen and (max-width: $media-phone) {
    gap: 10px;
  }

  .content {
    z-index: 3;
    width: 90%;
    margin: 0 auto;
    margin-top: 55vh;
    display: flex;
    flex-direction: column;
    gap: 40px;

    @media screen and (max-width: $media-phone) {
      gap: 30px;
    }
  }

  .text {
    text-align: center;
    width: 80%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 30px;

    @media screen and (max-width: $media-phone) {
      gap: 15px;
    }

    p {
      font-size: 1.7em;
      line-height: normal;
      font-family: v-bind(textFont), serif;

      b {
        font-family: v-bind(textFontB), sans-serif;
      }

      @media screen and (max-width: $media-phone) {
        font-size: 1.45em;
      }
    }
  }
}

.foot {
  color: v-bind(mainColor);
  background-color: v-bind(backgroundColor);
}
</style>
